// Game Utilities Service
// Helper functions for game state management and conversions

import { GameState, Move, PlayerColor } from './types';

/**
 * Game Engine Utilities
 */
export class GameUtils {
  /**
   * Convert backend game state to frontend format
   */
  static convertBackendGameState(backendState: GameState) {
    return {
      gameId: backendState.gameId,
      players: backendState.players,
      currentPlayerIndex: backendState.currentPlayerIndex,
      gameStatus: this.mapGamePhaseToStatus(backendState.gamePhase),
      gamePhase: backendState.gamePhase,
      winner: backendState.winner,
      lastDiceRoll: backendState.lastDiceRoll,
      createdAt: backendState.createdAt,
      updatedAt: backendState.updatedAt,
    };
  }

  /**
   * Map game phase to legacy status
   */
  static mapGamePhaseToStatus(
    phase: string
  ): 'waiting' | 'playing' | 'finished' {
    switch (phase) {
      case 'waiting':
        return 'waiting';
      case 'active':
        return 'playing';
      case 'finished':
        return 'finished';
      default:
        return 'waiting';
    }
  }

  /**
   * Map backend player color to frontend color
   */
  static mapPlayerColor(backendColor: string): PlayerColor {
    const colorMap: Record<string, PlayerColor> = {
      red: 'red',
      green: 'green',
      yellow: 'yellow',
      blue: 'blue',
    };

    return colorMap[backendColor.toLowerCase()] || 'red';
  }

  /**
   * Check if a piece can move based on available moves
   */
  static canPieceMove(pieceId: string, availableMoves: Move[]): boolean {
    return availableMoves.some((move) => move.pieceId === pieceId);
  }

  /**
   * Get valid moves for a specific piece
   */
  static getValidMovesForPiece(
    pieceId: string,
    availableMoves: Move[]
  ): Move[] {
    return availableMoves.filter((move) => move.pieceId === pieceId);
  }

  /**
   * Get valid moves for current player
   */
  static getValidMovesForCurrentPlayer(
    gameState: GameState,
    availableMoves: Move[]
  ): Move[] {
    if (!gameState.players[gameState.currentPlayerIndex]) {
      return [];
    }

    const currentPlayerId = gameState.players[gameState.currentPlayerIndex].id;
    return availableMoves.filter((move) => move.playerId === currentPlayerId);
  }

  /**
   * Check if it's a specific player's turn
   */
  static isPlayerTurn(gameState: GameState, playerId: string): boolean {
    const currentPlayer = gameState.players[gameState.currentPlayerIndex];
    return currentPlayer?.id === playerId;
  }

  /**
   * Get player by ID
   */
  static getPlayerById(gameState: GameState, playerId: string) {
    return gameState.players.find((player) => player.id === playerId);
  }

  /**
   * Get piece by ID for a player
   */
  static getPieceById(gameState: GameState, playerId: string, pieceId: string) {
    const player = this.getPlayerById(gameState, playerId);
    return player?.pieces.find((piece) => piece.id === pieceId);
  }

  /**
   * Check if game is finished
   */
  static isGameFinished(gameState: GameState): boolean {
    return gameState.gamePhase === 'finished' || !!gameState.winner;
  }

  /**
   * Check if game is waiting for players
   */
  static isGameWaiting(gameState: GameState): boolean {
    return gameState.gamePhase === 'waiting';
  }

  /**
   * Check if game is active
   */
  static isGameActive(gameState: GameState): boolean {
    return gameState.gamePhase === 'active';
  }

  /**
   * Get game progress (percentage of pieces finished)
   */
  static getGameProgress(gameState: GameState): number {
    const totalPieces = gameState.players.reduce(
      (total, player) => total + player.pieces.length,
      0
    );

    const finishedPieces = gameState.players.reduce(
      (total, player) =>
        total + player.pieces.filter((piece) => piece.position >= 100).length,
      0
    );

    return totalPieces > 0 ? (finishedPieces / totalPieces) * 100 : 0;
  }

  /**
   * Get player statistics
   */
  static getPlayerStats(gameState: GameState, playerId: string) {
    const player = this.getPlayerById(gameState, playerId);
    if (!player) return null;

    const homePieces = player.pieces.filter(
      (piece) => piece.position === -1
    ).length;
    const activePieces = player.pieces.filter(
      (piece) => piece.position >= 0 && piece.position < 100
    ).length;
    const finishedPieces = player.pieces.filter(
      (piece) => piece.position >= 100
    ).length;

    return {
      playerId: player.id,
      displayName: player.displayName,
      color: player.color,
      homePieces,
      activePieces,
      finishedPieces,
      totalPieces: player.pieces.length,
      isWinner: player.hasWon,
      isActive: player.isActive,
    };
  }

  /**
   * Format game duration
   */
  static formatGameDuration(createdAt: string, updatedAt?: string): string {
    const start = new Date(createdAt);
    const end = updatedAt ? new Date(updatedAt) : new Date();
    const duration = end.getTime() - start.getTime();

    const minutes = Math.floor(duration / 60000);
    const seconds = Math.floor((duration % 60000) / 1000);

    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  }
}
